import Link from 'next/link'
import Head from 'next/head'
import Seo, { SITE_URL } from '@comp/seo'
import JsonLd from '@comp/structured-data'
import Layout from '@comp/layout'
import FinalCta from '@comp/home/final-cta'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'

type Lang = 'en' | 'tr'

type LocalizedText = { en: string; tr: string }

type CompareCardData = {
  href: string
  title: LocalizedText
  description: LocalizedText
}

const ui = {
  eyebrow: { en: 'Compare', tr: 'Karşılaştırma' },
  titleLine1: {
    en: 'Choosing an identity vendor?',
    tr: 'Kimlik tedarikçisi mi seçiyorsunuz?',
  },
  titleLine2: { en: 'Compare honestly.', tr: 'Dürüstçe karşılaştırın.' },
  lede: {
    en: 'Feature-by-feature comparisons based on public information, including where competitors are strong and where Monofor is the better fit.',
    tr: 'Kamuya açık bilgilere dayalı, özellik özellik karşılaştırmalar; rakiplerin güçlü olduğu ve Monofor’un daha iyi bir tercih olduğu noktalar dahil.',
  },
  alternativesHeading: {
    en: 'Monofor as an alternative',
    tr: 'Alternatif olarak Monofor',
  },
  versusHeading: { en: 'Vendor vs vendor', tr: 'Tedarikçi tedarikçiye karşı' },
  rankingsHeading: { en: 'Market rankings', tr: 'Pazar sıralamaları' },
  readComparison: { en: 'Read the comparison', tr: 'Karşılaştırmayı okuyun' },
  seoTitle: {
    en: 'Compare Identity & PAM Vendors | Monofor',
    tr: 'Kimlik ve PAM Tedarikçilerini Karşılaştırın | Monofor',
  },
  seoDescription: {
    en: 'Honest comparisons of identity and privileged access vendors: Okta, CyberArk, Delinea, BeyondTrust, Entra ID; and where Monofor fits.',
    tr: 'Kimlik ve ayrıcalıklı erişim tedarikçilerinin dürüst karşılaştırmaları: Okta, CyberArk, Delinea, BeyondTrust, Entra ID; ayrıca Monofor’un konumu.',
  },
}

const alternatives: CompareCardData[] = [
  {
    href: '/compare/okta-alternative',
    title: { en: 'Okta alternative', tr: 'Okta alternatifi' },
    description: {
      en: 'Compare deployment, identity, PAM and AI agent controls, with product scope and source links.',
      tr: 'Kurulumu, kimlik yönetimini, PAM’i ve AI ajan kontrollerini ürün kapsamı ve kaynaklarıyla karşılaştırın.',
    },
  },
  {
    href: '/compare/cyberark-alternative',
    title: { en: 'CyberArk alternative', tr: 'CyberArk alternatifi' },
    description: {
      en: 'Enterprise PAM without the enterprise drag: vault, session recording, JIT; priced per session.',
      tr: 'Kurumsal hantallık olmadan kurumsal PAM: kasa, oturum kaydı, JIT; oturum başına fiyatlandırma.',
    },
  },
  {
    href: '/compare/delinea-alternative',
    title: { en: 'Delinea alternative', tr: 'Delinea alternatifi' },
    description: {
      en: 'The vault is table stakes. The platform (SSO, MFA, governance included) is the difference.',
      tr: 'Kasa artık asgari standart. Farkı yaratan platform: SSO, MFA ve yönetişim dahil.',
    },
  },
  {
    href: '/compare/beyondtrust-alternative',
    title: { en: 'BeyondTrust alternative', tr: 'BeyondTrust alternatifi' },
    description: {
      en: 'Privileged access without per-asset pricing: the bill tracks sessions, not your server count.',
      tr: 'Varlık başına fiyatlandırma olmadan ayrıcalıklı erişim: fatura sunucu sayınızı değil, oturumları izler.',
    },
  },
  {
    href: '/compare/adfs-alternative',
    title: { en: 'ADFS alternative', tr: 'ADFS alternatifi' },
    description: {
      en: 'Modern SAML and OIDC federation on top of your existing AD, after Microsoft moved on from AD FS.',
      tr: 'Mevcut AD’nizin üzerinde modern SAML ve OIDC federasyonu, Microsoft AD FS’i geride bırakırken.',
    },
  },
  {
    href: '/compare/keycloak-alternative',
    title: { en: 'Keycloak alternative', tr: 'Keycloak alternatifi' },
    description: {
      en: 'Keep the self-hosted control, drop the operational toil; governance, reporting, and support included.',
      tr: 'Self-hosted kontrolü koruyun, operasyonel yükü bırakın; yönetişim, raporlama ve destek dahil.',
    },
  },
  {
    href: '/compare/venafi-alternative',
    title: { en: 'Venafi alternative', tr: 'Venafi alternatifi' },
    description: {
      en: 'Certificate lifecycle inside the PAM platform you already run, not another enterprise silo.',
      tr: 'Zaten işlettiğiniz PAM platformunun içinde sertifika yaşam döngüsü, ayrı bir kurumsal silo değil.',
    },
  },
  {
    href: '/compare/teleport-alternative',
    title: { en: 'Teleport alternative', tr: 'Teleport alternatifi' },
    description: {
      en: 'The infrastructure access engineers expect, plus the vault, video, and approvals auditors require.',
      tr: 'Mühendislerin beklediği altyapı erişimi, artı denetçilerin aradığı kasa, video ve onaylar.',
    },
  },
  {
    href: '/compare/sailpoint-alternative',
    title: { en: 'SailPoint alternative', tr: 'SailPoint alternatifi' },
    description: {
      en: 'IGA built into the IdP: lifecycle, certifications, and SoD without a second platform or a services program.',
      tr: 'IdP’ye yerleşik IGA: ikinci bir platform veya danışmanlık programı olmadan yaşam döngüsü, sertifikasyon ve SoD.',
    },
  },
]

const versus: CompareCardData[] = [
  {
    href: '/compare/cyberark-vs-delinea',
    title: { en: 'CyberArk vs Delinea', tr: 'CyberArk vs Delinea' },
    description: {
      en: 'Two PAM weight classes compared honestly, and the question both of them skip.',
      tr: 'İki PAM ağırlık sınıfının dürüst karşılaştırması ve ikisinin de atladığı soru.',
    },
  },
  {
    href: '/compare/okta-vs-entra-id',
    title: { en: 'Okta vs Entra ID', tr: 'Okta vs Entra ID' },
    description: {
      en: 'Neutrality vs ecosystem gravity, and the deployment question neither can answer.',
      tr: 'Tarafsızlığa karşı ekosistem çekimi ve ikisinin de yanıtlayamadığı kurulum sorusu.',
    },
  },
]

const rankings: CompareCardData[] = [
  {
    href: '/compare/best-pam-solutions',
    title: {
      en: 'Best PAM solutions 2026',
      tr: "2026'nın en iyi PAM çözümleri",
    },
    description: {
      en: 'Eight privileged-access platforms ranked honestly, from enterprise suites to developer proxies.',
      tr: 'Sekiz ayrıcalıklı erişim platformunun dürüst sıralaması: kurumsal paketlerden geliştirici proxy’lerine.',
    },
  },
  {
    href: '/compare/best-okta-alternatives',
    title: {
      en: 'Best Okta alternatives 2026',
      tr: "2026'nın en iyi Okta alternatifleri",
    },
    description: {
      en: 'Eight identity providers compared, including when Microsoft or open source beats buying anything.',
      tr: 'Sekiz kimlik sağlayıcının karşılaştırması; Microsoft’un veya açık kaynağın kazandığı durumlar dahil.',
    },
  },
  {
    href: '/compare/best-certificate-lifecycle-management',
    title: {
      en: 'Best CLM tools 2026',
      tr: "2026'nın en iyi CLM araçları",
    },
    description: {
      en: 'Eight certificate lifecycle platforms ranked, as 47-day TLS lifetimes make automation mandatory.',
      tr: '47 günlük TLS ömürleri otomasyonu zorunlu kılarken sekiz sertifika yaşam döngüsü platformunun sıralaması.',
    },
  },
  {
    href: '/compare/best-itdr-solutions',
    title: {
      en: 'Best ITDR solutions 2026',
      tr: "2026'nın en iyi ITDR çözümleri",
    },
    description: {
      en: 'Eight identity threat detection platforms compared, from EDR-vendor modules to IdP-native response.',
      tr: 'Sekiz kimlik tehdidi tespit platformunun karşılaştırması: EDR modüllerinden IdP’ye yerleşik müdahaleye.',
    },
  },
  {
    href: '/compare/best-iga-tools',
    title: {
      en: 'Best IGA tools 2026',
      tr: "2026'nın en iyi IGA araçları",
    },
    description: {
      en: 'Eight identity governance platforms ranked, from services-led suites to IGA built into the IdP.',
      tr: 'Sekiz kimlik yönetişimi platformunun sıralaması: danışmanlık odaklı paketlerden IdP’ye yerleşik IGA’ya.',
    },
  },
]

type Props = {
  locale: string
}

export default function CompareIndexPage({ locale }: Props) {
  const lang: Lang = locale === 'tr' ? 'tr' : 'en'
  const basePath = lang === 'tr' ? '/tr' : ''
  const cards = [...alternatives, ...versus, ...rankings]
  return (
    <Layout>
      <Seo
        title={ui.seoTitle[lang]}
        description={ui.seoDescription[lang]}
        path={`${basePath}/compare`}
      />
      <Head>
        <link rel="alternate" hrefLang="en" href={`${SITE_URL}/compare`} />
        <link rel="alternate" hrefLang="tr" href={`${SITE_URL}/tr/compare`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/compare`} />
      </Head>
      <JsonLd id="comparison-directory" data={{
        '@type': 'CollectionPage', '@id': `${SITE_URL}${basePath}/compare#webpage`,
        url: `${SITE_URL}${basePath}/compare`, name: ui.seoTitle[lang],
        description: ui.seoDescription[lang], inLanguage: lang,
        publisher: { '@type': 'Organization', name: 'Monofor', url: SITE_URL },
        mainEntity: {
          '@type': 'ItemList', itemListOrder: 'https://schema.org/ItemListUnordered',
          numberOfItems: cards.length,
          itemListElement: cards.map((item, i) => ({
            '@type': 'ListItem', position: i + 1,
            name: item.title[lang], description: item.description[lang],
            url: `${SITE_URL}${rankings.includes(item) ? '' : basePath}${item.href}`,
          })),
        },
      }} />
      <main className="bg-surface text-ink-primary" lang={lang}>
        <Section tone="default" density="compact" className="relative overflow-hidden">
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute -top-32 -right-24 h-[640px] w-[640px] rounded-full bg-brand-100/60 blur-3xl" />
            <div className="absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-accent-100/50 blur-3xl" />
          </div>
          <Container size="xl" className="relative">
            <div className="max-w-3xl">
              <Eyebrow tone="brand">{ui.eyebrow[lang]}</Eyebrow>
              <div className="mt-6">
                <Heading as="h1" size="display-xl">
                  {ui.titleLine1[lang]}
                  <br />
                  {ui.titleLine2[lang]}
                </Heading>
              </div>
              <div className="mt-8 max-w-2xl">
                <Text size="xl" tone="secondary">
                  {ui.lede[lang]}
                </Text>
              </div>
            </div>
          </Container>
        </Section>

        <Section tone="subtle" density="default">
          <Container size="xl">
            <Heading as="h2" size="display-sm">
              {ui.alternativesHeading[lang]}
            </Heading>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {alternatives.map((item) => (
                <CompareCard key={item.href} item={item} lang={lang} />
              ))}
            </div>

            <Heading as="h2" size="display-sm" className="mt-16">
              {ui.versusHeading[lang]}
            </Heading>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {versus.map((item) => (
                <CompareCard key={item.href} item={item} lang={lang} />
              ))}
            </div>

            <Heading as="h2" size="display-sm" className="mt-16">
              {ui.rankingsHeading[lang]}
            </Heading>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {rankings.map((item) => (
                <CompareCard key={item.href} item={item} lang={lang} englishOnly />
              ))}
            </div>
          </Container>
        </Section>

        <FinalCta />
      </main>
    </Layout>
  )
}

function CompareCard({ item, lang, englishOnly = false }: { item: CompareCardData; lang: Lang; englishOnly?: boolean }) {
  return (
    <Link
      href={item.href}
      locale={englishOnly ? 'en' : lang}
      hrefLang={englishOnly ? 'en' : lang}
      className="group flex h-full flex-col rounded-2xl border border-line-subtle bg-surface p-6 shadow-card transition duration-300 ease-smooth hover:-translate-y-0.5 hover:border-line hover:shadow-card-hover lg:p-8"
    >
      <span className="text-body-lg font-medium text-ink-primary group-hover:text-brand-800">
        {item.title[lang]}
      </span>
      {englishOnly && lang === 'tr' && <span className="mt-2 text-xs font-medium text-brand-700">İngilizce içerik</span>}
      <span className="mt-2 flex-grow text-body-sm text-ink-tertiary">
        {item.description[lang]}
      </span>
      <span className="mt-4 inline-flex items-center gap-1.5 text-body-sm font-medium text-brand-700 transition-transform group-hover:translate-x-0.5">
        {ui.readComparison[lang]}
        <span aria-hidden>→</span>
      </span>
    </Link>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
